// hooks/useUserAuth.js

import { useEffect, useState } from "react";
import { fetchUserMetadata } from "../api/user";
import { useWalletConnect } from "../WalletConnectContext";

const userCache = new Map();
const userRequests = new Map();

function normalizeAccountId(value) {
  return String(value || "").trim().toLowerCase();
}

function loadProtectedUser(accountId, selector) {
  const normalized = normalizeAccountId(accountId);
  if (!normalized || !selector) return Promise.resolve(null);
  if (userCache.has(normalized)) return Promise.resolve(userCache.get(normalized));
  if (userRequests.has(normalized)) return userRequests.get(normalized);

  const request = fetchUserMetadata(normalized, { selector })
    .then((data) => {
      const user = { near_wallet: normalized, ...data };
      userCache.set(normalized, user);
      return user;
    })
    .finally(() => {
      userRequests.delete(normalized);
    });
  userRequests.set(normalized, request);
  return request;
}

export function invalidateUserAuthCache(accountId = "") {
  const normalized = normalizeAccountId(accountId);
  if (normalized) userCache.delete(normalized);
  else userCache.clear();
}

export default function useUserAuth({ enabled = true } = {}) {
  const { accountId, selector } = useWalletConnect();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let disposed = false;
    const normalized = normalizeAccountId(accountId);
    if (!enabled) {
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
      return undefined;
    }
    if (!normalized) {
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
      return undefined;
    }
    if (!selector) {
      setLoading(true);
      return undefined;
    }

    setLoading(true);
    loadProtectedUser(normalized, selector)
      .then((data) => {
        if (disposed) return;
        if (data) {
          setUser(data);
          setIsAdmin(Number(data.role) === 1);
        } else {
          setUser({ near_wallet: normalized, role: 0 });
          setIsAdmin(false);
        }
      })
      .catch((err) => {
        if (disposed) return;
        console.warn("useUserAuth: protected user load failed", err);
        setUser({ near_wallet: normalized, role: 0 });
        setIsAdmin(false);
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, [accountId, selector, enabled]);

  return {
    user,
    isAdmin,
    accountId,
    loading,
  };
}
